import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_WEEK_START_DAY,
  WeekStartDayType,
  getSavedWeekStartDay,
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
} from '../../../../../../data-access';
import { ReactComponent as CalendarIcon } from '../../../../../../assets/tabs/settings-tab/calendar-icon.svg';
import './WeekStartDay.scss';

const WEEK_START_DAY_OPTIONS: { value: WeekStartDayType; labelKey: string }[] = [
  { value: 'sunday', labelKey: 'settings.calendar.weekStartDay.sunday' },
  { value: 'monday', labelKey: 'settings.calendar.weekStartDay.monday' },
  { value: 'saturday', labelKey: 'settings.calendar.weekStartDay.saturday' },
];

const WeekStartDay: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { calendarPreferences } = useCalendarContext();
  const savedWeekStartDay = getSavedWeekStartDay(calendarPreferences);

  const [weekStartDay, setWeekStartDay] = useState<WeekStartDayType>(DEFAULT_WEEK_START_DAY);

  useEffect(() => {
    setWeekStartDay(savedWeekStartDay);
  }, [savedWeekStartDay]);

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: WeekStartDayType) => {
        if (!user.preferenceId) {
          return;
        }

        // `weekStartDay` is added to CalendarPreference in @demind-inc/core 1.11.13.
        return updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences: { weekStartDay: value } as Record<string, unknown>,
        });
      }, 500),
    [updateCalendarPref, user.preferenceId]
  );

  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  const handleChange = useCallback(
    (value: WeekStartDayType) => {
      setWeekStartDay(value);
      debouncedUpdate(value);
    },
    [debouncedUpdate]
  );

  return (
    <Stack id="weekStartDay" gap={2} className="week-start-day" sx={{ scrollMarginTop: '12px' }}>
      <Stack gap={1} direction="row" alignItems="center">
        <CalendarIcon />
        <Typography variant="body1" className="week-start-day__section-title">
          {t('settings.calendar.weekStartDay.title')}
        </Typography>
      </Stack>

      <Typography variant="subtitle2">{t('settings.calendar.weekStartDay.description')}</Typography>

      <RadioGroup
        value={weekStartDay}
        onChange={(event) => handleChange(event.target.value as WeekStartDayType)}
        className="week-start-day__radio-group"
      >
        {WEEK_START_DAY_OPTIONS.map((option, index) => (
          <Stack key={option.value}>
            <FormControlLabel
              value={option.value}
              control={<Radio size="small" className="week-start-day__radio-item__control" />}
              label={
                <Typography variant="body1" className="week-start-day__radio-item__label">
                  {t(option.labelKey)}
                </Typography>
              }
              className="week-start-day__radio-item"
            />
            {index !== WEEK_START_DAY_OPTIONS.length - 1 && (
              <Divider className="week-start-day__radio-item__divider" />
            )}
          </Stack>
        ))}
      </RadioGroup>
    </Stack>
  );
};

export default WeekStartDay;
